import { useEffect, useRef, useState } from 'react';
import CheckIcon from '@/assets/icons/check.svg?react';
import { CodexWebhookRows, useStartCodexConnection } from '@/features/connections';
import { useViewer } from '@/features/session';
import { formatClock } from '@/shared/lib/format';
import type { CodexConnectionData } from '@/shared/types/workspace';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { Spinner } from '@/shared/ui/spinner';

type CodexStepProps = { connection: CodexConnectionData; onContinue: () => void };

export function CodexStep({ connection, onContinue }: CodexStepProps) {
  const { startCodexConnection } = useStartCodexConnection();
  const { refreshViewer } = useViewer();
  const [isChecking, setIsChecking] = useState(false);
  const hasStartedRef = useRef(false);
  const isOff = connection.status === 'NOT_CONNECTED';

  // The webhook URL and secret exist from the moment someone picks Codex.
  useEffect(() => {
    if (!isOff || hasStartedRef.current) return;
    hasStartedRef.current = true;
    void startCodexConnection();
  }, [isOff, startCodexConnection]);

  const checkAgain = async () => {
    setIsChecking(true);
    try {
      await refreshViewer();
    } finally {
      setIsChecking(false);
    }
  };

  if (isOff || !connection.webhookUrl) {
    return (
      <p className="flex items-center gap-2 text-muted" role="status">
        <Spinner /> Making the webhook URL and secret.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-muted">
        Paste this URL and secret into your Agents API environment settings.
      </span>
      <CodexWebhookRows webhookUrl={connection.webhookUrl} secretHint={connection.secretHint} />
      {connection.status === 'CONNECTED' ? (
        <Banner
          tone="ok"
          size="compact"
          icon={<CheckIcon className="size-4 shrink-0 text-ok-text" />}
          title={
            connection.lastEventAt
              ? `Received ${connection.lastEventType ?? 'an event'} at ${formatClock(connection.lastEventAt)}.`
              : 'Received the first event.'
          }
          action={
            <Button size="sm" variant="primary" onClick={onContinue}>
              Continue
            </Button>
          }
        >
          Codex is connected.
        </Banner>
      ) : (
        <Banner
          tone="info"
          size="compact"
          icon={<Spinner className="text-info-text" />}
          title="Waiting for the first event from Codex."
          action={
            <Button size="sm" isLoading={isChecking} onClick={() => void checkAgain()}>
              Check again
            </Button>
          }
        >
          Start any Codex task in this environment, or send a test event from your Agents API
          settings.
        </Banner>
      )}
    </div>
  );
}
